import React from 'react';
import { Download, Users, Database, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';

const UsageCard = ({ name, usage, limit, unit, description, isBasicPlan }) => {
  const getIcon = () => {
    if (name?.includes('Export')) return <Download className="w-6 h-6" />;
    if (name?.includes('Caregiver')) return <Users className="w-6 h-6" />;
    if (name?.includes('Storage')) return <Database className="w-6 h-6" />;
    if (name?.includes('Analytics')) return <TrendingUp className="w-6 h-6" />;
    return <CheckCircle className="w-6 h-6" />;
  };

  const getProgressColor = () => {
    if (limit === 0) return 'bg-gray-300';
    const percentage = (usage / limit) * 100;
    if (percentage >= 90) return 'bg-red-600';
    if (percentage >= 70) return 'bg-yellow-500';
    return 'bg-green-600';
  };

  const getStatusColor = () => {
    if (limit === 0) return 'text-gray-500';
    const percentage = (usage / limit) * 100;
    if (percentage >= 90) return 'text-red-600';
    if (percentage >= 70) return 'text-yellow-600';
    return 'text-green-600';
  };

  const percentage = limit > 0 ? Math.min((usage / limit) * 100, 100) : 0;
  const isLimitReached = usage >= limit && limit > 0;
  const isApproachingLimit = percentage >= 70 && percentage < 100;

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`${getStatusColor()}`}>
            {getIcon()}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{name}</h3>
            <p className="text-sm text-gray-600">{description}</p>
          </div>
        </div>
        {isLimitReached && (
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
        )}
      </div>

      {/* Usage Stats */}
      <div className="mb-4">
        <div className="flex items-baseline justify-between mb-2">
          <div>
            <span className="text-3xl font-bold text-gray-900">{usage}</span>
            <span className="text-gray-600 ml-2">/ {limit === 0 ? 'Unavailable' : limit}</span>
          </div>
          <span className="text-sm text-gray-600">{unit}</span>
        </div>

        {/* Progress Bar */}
        {limit > 0 ? (
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`${getProgressColor()} h-3 rounded-full transition-all duration-300`}
              style={{ width: `${percentage}%` }}
            />
          </div>
        ) : (
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div className="bg-gray-300 h-3 rounded-full w-0" />
          </div>
        )}
      </div>

      {/* Status Messages */}
      {isLimitReached && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
          <p className="text-sm text-red-800 font-medium">
            Limit reached. Upgrade to continue using this feature.
          </p>
        </div>
      )}

      {isApproachingLimit && !isLimitReached && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
          <p className="text-sm text-yellow-800 font-medium">
            Approaching limit. {limit - usage} {unit} remaining.
          </p>
        </div>
      )}

      {limit === 0 && isBasicPlan && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <p className="text-sm text-gray-700 font-medium">
            Not available in Basic plan. Upgrade to unlock.
          </p>
        </div>
      )}

      {/* Remaining Count */}
      {limit > 0 && !isLimitReached && (
        <div className="text-sm text-gray-600">
          <span className="font-medium">{limit - usage}</span> {unit} remaining this month
        </div>
      )}
    </div>
  );
};

export default UsageCard;